import { t } from "@lingui/macro";
import { format, parseISO } from "date-fns";

import {
  RecurrenceAvailabilityPreview,
  RecurrenceFormValue,
} from "@/features/RecurringRequests/types";
import { Button } from "@whoosh/design-system/src/components/controls";
import { InfoBannerCautionMinorIcon } from "@whoosh/design-system/src/components/display/InfoBanner";
import {
  BlockIcon,
  CheckIcon,
  ChevronLeftIcon,
  ClockIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import { StandardModal } from "@whoosh/design-system/src/components/overlay/StandardModal";
import {
  Box,
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import {
  formatStatusTime,
  formatTime,
  formatTimeRange,
  recurringRequestDatesHeaderCadenceAndTime,
} from "../../formatters";
import { SavedRequestSeries } from "./SavedRequestSeries";

export type RequestTimes = {
  time?: string | null;
  minTime?: string | null;
  maxTime?: string | null;
};

type RecurringRequestDatesModalProps = {
  savedBookingId?: string;
  request?: RequestTimes | null;
  unchangedRequest?: RequestTimes | null;
  recurrenceType?: RecurrenceFormValue["recurrenceType"] | null;
  repeatInterval?: number | null;
  courseLabel?: string | null;
  availableOccurrences: RecurrenceAvailabilityPreview["included"];
  unavailableDates: RecurrenceAvailabilityPreview["excluded"];
  removedOccurrences?: Array<{ date: string }>;
  unchangedOccurrences?: RecurrenceAvailabilityPreview["included"];
  isOpen: boolean;
  onClose: () => void;
  mode?: "view" | "confirm";
  onBack?: () => void;
  onConfirm?: () => void;
  isConfirming?: boolean;
};

const requestRowTimeLabel = (request?: RequestTimes | null) => {
  const requestedTime = formatTime(request?.time);
  const requestedWindow = formatTimeRange(request?.minTime, request?.maxTime);
  return requestedWindow ?? requestedTime;
};

export const RecurringRequestDatesModal = ({
  request,
  savedBookingId,
  unchangedRequest,
  recurrenceType,
  repeatInterval,
  courseLabel,
  availableOccurrences,
  unavailableDates,
  removedOccurrences = [],
  unchangedOccurrences = [],
  isOpen,
  onClose,
  mode = "view",
  onBack,
  onConfirm,
  isConfirming = false,
}: RecurringRequestDatesModalProps) => {
  const isConfirmMode = mode === "confirm";
  const requestedTime = formatTime(request?.time);
  const requestedWindow = formatTimeRange(request?.minTime, request?.maxTime);
  const timeLabel = savedBookingId ? null : requestedTime ?? requestedWindow;
  const cadenceAndTime = recurringRequestDatesHeaderCadenceAndTime({
    recurrenceType,
    repeatInterval,
    timeLabel,
  });
  const headerCourseLabel = savedBookingId ? null : courseLabel;
  const hasHeaderSubtitle = !!cadenceAndTime || !!headerCourseLabel;
  const availableTitle = isConfirmMode
    ? t`Updated requests`
    : t`Available Dates`;
  const unavailableTitle = isConfirmMode
    ? t`New requested dates that are unavailable`
    : t`Unavailable Dates`;
  const unavailableSubtitle = isConfirmMode
    ? t`These dates will not be submitted.`
    : t`These dates will not be submitted.`;
  const updatedRequestTimeLabel = requestRowTimeLabel(request);
  const unchangedRequestTimeLabel = requestRowTimeLabel(
    unchangedRequest ?? request
  );

  return (
    <StandardModal
      isOpen={isOpen}
      onClose={isConfirmMode ? onBack ?? onClose : onClose}
      hideCloseButton
      hideHeaderPadding
      hideFooterPadding
      backgroundColorOverride="surface-secondary"
      upperNode={
        <Box
          backgroundColor="surface-primary"
          flexGrow={1}
          minWidth={0}
          overflow="hidden"
          width="100%"
          paddingHorizontal={4}
          paddingTop={6}
          paddingBottom={4}
        >
          <HStack alignItems="flex-start" gap={3} width="100%">
            {isConfirmMode ? null : (
              <Box flexShrink={0}>
                <Button
                  size="regular"
                  accessibilityLabel={t`Modal Close Button`}
                  testID="modal-close-btn"
                  variant="secondary"
                  onPress={onClose}
                  leftIcon={<ChevronLeftIcon />}
                />
              </Box>
            )}
            <VStack
              flex={1}
              gap={1}
              paddingTop={1}
              minWidth={0}
              overflow="hidden"
            >
              <Text
                variant="mainHeading"
                numberOfLines={1}
                style={{
                  fontSize: 17,
                  lineHeight: 22,
                  fontFamily: "Inter_600SemiBold",
                }}
              >
                {t`Recurring request dates`}
              </Text>
              {hasHeaderSubtitle ? (
                <HStack alignItems="center" minWidth={0} width="100%">
                  {cadenceAndTime ? (
                    <Text
                      variant="mainSmallMedium"
                      color="text-secondary"
                      flexShrink={headerCourseLabel ? 0 : 1}
                      numberOfLines={1}
                    >
                      {cadenceAndTime}
                      {headerCourseLabel ? " · " : ""}
                    </Text>
                  ) : null}
                  {headerCourseLabel ? (
                    <Text
                      variant="mainSmallMedium"
                      color="text-secondary"
                      numberOfLines={1}
                      ellipsizeMode="tail"
                      flex={1}
                      flexShrink={1}
                      minWidth={0}
                    >
                      {headerCourseLabel}
                    </Text>
                  ) : null}
                </HStack>
              ) : null}
            </VStack>
          </HStack>
        </Box>
      }
      content={
        <VStack padding={4} gap={5} testID="recurring-request-dates-modal">
          {savedBookingId ? (
            isOpen ? (
              <SavedRequestSeries
                bookingId={savedBookingId}
                onClose={onClose}
              />
            ) : null
          ) : isConfirmMode ? (
            <>
              {unavailableDates.length > 0 ? (
                <UnavailableDatesSection
                  title={unavailableTitle}
                  subtitle={unavailableSubtitle}
                  unavailableDates={unavailableDates}
                  timeLabel={updatedRequestTimeLabel}
                />
              ) : null}

              {removedOccurrences.length > 0 ? (
                <RemovedDatesSection occurrences={removedOccurrences} />
              ) : null}

              {availableOccurrences.length > 0 ? (
                <AvailableDatesSection
                  title={availableTitle}
                  occurrences={availableOccurrences}
                  timeLabel={updatedRequestTimeLabel}
                  testIDPrefix="recurring-request-available-date"
                />
              ) : null}

              {unchangedOccurrences.length > 0 ? (
                <AvailableDatesSection
                  title={t`Unchanged requests`}
                  occurrences={unchangedOccurrences}
                  timeLabel={unchangedRequestTimeLabel}
                  testIDPrefix="recurring-request-unchanged-date"
                />
              ) : null}
            </>
          ) : (
            <>
              {availableOccurrences.length > 0 ? (
                <AvailableDatesSection
                  title={availableTitle}
                  occurrences={availableOccurrences}
                  timeLabel={updatedRequestTimeLabel}
                  testIDPrefix="recurring-request-available-date"
                />
              ) : null}

              {unavailableDates.length > 0 ? (
                <UnavailableDatesSection
                  title={unavailableTitle}
                  subtitle={unavailableSubtitle}
                  unavailableDates={unavailableDates}
                  timeLabel={updatedRequestTimeLabel}
                />
              ) : null}

              {removedOccurrences.length > 0 ? (
                <RemovedDatesSection occurrences={removedOccurrences} />
              ) : null}
            </>
          )}
        </VStack>
      }
      footerContent={
        isConfirmMode ? (
          <HStack
            gap={4}
            justifyContent="space-between"
            padding={5}
            borderTopWidth={1}
            borderColor="outline-default"
            backgroundColor="surface-primary"
          >
            <Button
              variant="white"
              size="small"
              accessibilityLabel={t`Back to recurring request update`}
              testID="recurring-request-dates-confirm-back"
              onPress={onBack ?? onClose}
            >
              {t`Back`}
            </Button>
            <Button
              size="small"
              accessibilityLabel={t`Confirm recurring request update`}
              testID="recurring-request-dates-confirm-button"
              isLoading={isConfirming}
              onPress={onConfirm}
            >
              {t`Confirm`}
            </Button>
          </HStack>
        ) : null
      }
    />
  );
};

const AvailableDatesSection = ({
  title,
  occurrences,
  timeLabel,
  testIDPrefix,
}: {
  title: string;
  occurrences: RecurrenceAvailabilityPreview["included"];
  timeLabel: string | null;
  testIDPrefix: string;
}) => (
  <VStack gap={2}>
    <Text variant="mainBaseSemiBold">{title}</Text>
    <VStack
      backgroundColor="surface-primary"
      borderWidth={1}
      borderColor="surface-dividers"
      borderRadius="4"
      overflow="hidden"
    >
      {occurrences.map((occurrence, index) => {
        const warning = occurrence.warnings[0];
        const date = parseISO(occurrence.date);

        return (
          <VStack
            key={occurrence.date}
            gap={2}
            padding={4}
            borderBottomWidth={index < occurrences.length - 1 ? 1 : 0}
            borderBottomColor="surface-dividers"
            testID={`${testIDPrefix}-${occurrence.date}`}
          >
            <HStack alignItems="center" gap={3}>
              <Box
                width={26}
                height={26}
                borderRadius="8"
                backgroundColor={
                  warning
                    ? "surface-status-warning-subtler"
                    : "surface-status-success-subtler"
                }
                alignItems="center"
                justifyContent="center"
              >
                {warning ? (
                  <InfoBannerCautionMinorIcon
                    width={14}
                    height={14}
                    color="icon-on-color-warning"
                    testID={`recurring-request-date-warning-${occurrence.date}`}
                  />
                ) : (
                  <CheckIcon
                    width={20}
                    height={20}
                    color="icon-on-color-success"
                    testID={`recurring-request-date-check-${occurrence.date}`}
                  />
                )}
              </Box>
              <VStack gap={1} flex={1}>
                <HStack justifyContent="space-between">
                  <Text variant="mainBaseSemiBold">
                    {format(date, "EEE, MMM do")}
                  </Text>
                  <Text
                    variant="mainBaseMedium"
                    color="text-secondary"
                    textAlign="right"
                  >
                    {timeLabel}
                  </Text>
                </HStack>

                {occurrence.warnings.length > 0 ? (
                  <VStack
                    gap={1}
                    paddingHorizontal="2.5"
                    paddingVertical={2}
                    borderRadius="2"
                    backgroundColor="surface-status-warning-subtlest"
                  >
                    {occurrence.warnings.map((status) => {
                      const statusTime = formatStatusTime(
                        status.startAt,
                        status.endAt
                      );

                      return (
                        <HStack
                          key={`${occurrence.date}-${status.type}-${status.label}`}
                          justifyContent="space-between"
                          alignItems="center"
                          gap={2}
                        >
                          <Text
                            variant="mainSmallMedium"
                            color="text-secondary"
                            flex={1}
                            numberOfLines={1}
                            ellipsizeMode="tail"
                          >
                            {status.label}
                          </Text>
                          {statusTime ? (
                            <Text
                              variant="mainSmallMedium"
                              textAlign="right"
                              color="text-secondary"
                              flexShrink={0}
                            >
                              {statusTime}
                            </Text>
                          ) : null}
                        </HStack>
                      );
                    })}
                  </VStack>
                ) : null}
              </VStack>
            </HStack>
          </VStack>
        );
      })}
    </VStack>
  </VStack>
);

const UnavailableDatesSection = ({
  title,
  subtitle,
  unavailableDates,
  timeLabel,
}: {
  title: string;
  subtitle: string;
  unavailableDates: RecurrenceAvailabilityPreview["excluded"];
  timeLabel: string | null;
}) => (
  <VStack gap={2}>
    <VStack gap={1}>
      <Text variant="mainBaseSemiBold">{title}</Text>
      <Text variant="mainSmallMedium" color="text-secondary">
        {subtitle}
      </Text>
    </VStack>
    <VStack
      backgroundColor="surface-primary"
      borderWidth={1}
      borderColor="surface-dividers"
      borderRadius="4"
      overflow="hidden"
    >
      {unavailableDates.map((exclusion, index) => {
        const date = parseISO(exclusion.date);
        const isConflict = !!exclusion.conflict;
        const conflictTime = exclusion.conflict
          ? formatStatusTime(
              exclusion.conflict.startAt,
              exclusion.conflict.endAt
            )
          : null;
        const requestOpensReason = exclusion.requestOpensDate
          ? t`Requests for this date open ${format(
              parseISO(exclusion.requestOpensDate),
              "MMM do"
            )}`
          : null;
        const fallbackReason = t`Requests are not accepted at this time`;

        return (
          <HStack
            key={exclusion.date}
            gap={3}
            padding={4}
            borderBottomWidth={index < unavailableDates.length - 1 ? 1 : 0}
            borderBottomColor="surface-dividers"
            testID={`recurring-request-unavailable-date-${exclusion.date}`}
            alignItems="center"
          >
            <Box
              width={26}
              height={26}
              borderRadius="8"
              backgroundColor={
                isConflict
                  ? "surface-status-error-subtler"
                  : "surface-main-secondary-subtlest"
              }
              alignItems="center"
              justifyContent="center"
            >
              {isConflict ? (
                <BlockIcon
                  width={16}
                  height={16}
                  color="icon-on-color-error"
                  testID={`recurring-request-date-blocked-${exclusion.date}`}
                />
              ) : (
                <ClockIcon
                  width={16}
                  height={16}
                  color="main-secondary-800"
                  testID={`recurring-request-date-unavailable-${exclusion.date}`}
                />
              )}
            </Box>
            <VStack gap={1} flex={1}>
              <HStack justifyContent="space-between">
                <Text variant="mainBaseSemiBold">
                  {format(date, "EEE, MMM do")}
                </Text>
                <Text
                  variant="mainBaseMedium"
                  color="text-secondary"
                  textAlign="right"
                >
                  {timeLabel}
                </Text>
              </HStack>
              {requestOpensReason ? (
                <Text variant="mainSmallMedium" color="text-secondary">
                  {requestOpensReason}
                </Text>
              ) : exclusion.conflict ? (
                <VStack
                  gap={1}
                  paddingHorizontal="2.5"
                  paddingVertical={2}
                  borderRadius="2"
                  backgroundColor="surface-status-error-subtlest"
                >
                  <HStack
                    justifyContent="space-between"
                    alignItems="center"
                    gap={2}
                  >
                    <Text
                      variant="mainSmallMedium"
                      color="text-secondary"
                      flex={1}
                      numberOfLines={1}
                      ellipsizeMode="tail"
                    >
                      {exclusion.conflict.label}
                    </Text>
                    {conflictTime ? (
                      <Text
                        variant="mainSmallMedium"
                        color="text-secondary"
                        textAlign="right"
                        flexShrink={0}
                      >
                        {conflictTime}
                      </Text>
                    ) : null}
                  </HStack>
                </VStack>
              ) : (
                <Text variant="mainSmallMedium" color="text-secondary">
                  {fallbackReason}
                </Text>
              )}
            </VStack>
          </HStack>
        );
      })}
    </VStack>
  </VStack>
);

const RemovedDatesSection = ({
  occurrences,
}: {
  occurrences: Array<{ date: string }>;
}) => (
  <VStack gap={2}>
    <VStack gap={1}>
      <Text variant="mainBaseSemiBold">{t`Future instances being removed`}</Text>
      <Text variant="mainSmallMedium" color="text-secondary">
        {t`These existing requests will be removed from the series.`}
      </Text>
    </VStack>
    <VStack
      backgroundColor="surface-primary"
      borderWidth={1}
      borderColor="surface-dividers"
      borderRadius="4"
      overflow="hidden"
    >
      {occurrences.map((occurrence, index) => {
        const date = parseISO(occurrence.date);

        return (
          <HStack
            key={occurrence.date}
            gap={3}
            padding={4}
            borderBottomWidth={index < occurrences.length - 1 ? 1 : 0}
            borderBottomColor="surface-dividers"
            testID={`recurring-request-removed-date-${occurrence.date}`}
            alignItems="center"
          >
            <Box
              width={26}
              height={26}
              borderRadius="8"
              backgroundColor="surface-status-error-subtler"
              alignItems="center"
              justifyContent="center"
            >
              <BlockIcon
                width={16}
                height={16}
                color="icon-on-color-error"
                testID={`recurring-request-date-removed-${occurrence.date}`}
              />
            </Box>
            <Text variant="mainBaseSemiBold">
              {format(date, "EEE, MMM do")}
            </Text>
          </HStack>
        );
      })}
    </VStack>
  </VStack>
);
